import { HOME_AUDIENCES, WIDGET_HOME_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Card, CardBody, CardHeader, CardTitle, Field, Input, Select, Switch } from "@sagot/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { readMessengerProfile } from "./home-model";
import { HomeMessenger } from "./home-messenger";
import { HomeOptions } from "./home-options";
import { useAgentBookingActions, useHomeWorkflows } from "./use-home-data";
import type { WidgetOut } from "../shared/types";
import type { WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio.home";

function Counter({ name, max }: { name: "home.greeting" | "home.subtitle"; max: number }) {
  const { t } = useTranslation();
  const { control } = useFormContext<WidgetForm>();
  const value = useWatch({ control, name });
  return <span className="font-mono">{t(`${K}.screen.counter`, { count: value.length, max })}</span>;
}

/** Settings card: Home on/off, who sees it, greeting and subtitle (with live counters). */
function HomeScreen() {
  const { t } = useTranslation();
  const { control, register } = useFormContext<WidgetForm>();
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{t(`${K}.screen.title`)}</CardTitle>
          <p className="text-xs text-fg-subtle">{t(`${K}.screen.hint`)}</p>
        </div>
        <Controller control={control} name="home.enabled" render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} aria-label={t(`${K}.screen.enable`)} />} />
      </CardHeader>
      <CardBody className="space-y-4">
        <Field label={t(`${K}.screen.greeting`)} hint={<Counter name="home.greeting" max={WIDGET_HOME_LIMITS.greeting} />}>
          <Input maxLength={WIDGET_HOME_LIMITS.greeting} placeholder={t(`${K}.screen.greetingPlaceholder`)} {...register("home.greeting")} />
        </Field>
        <Field label={t(`${K}.screen.subtitle`)} hint={<Counter name="home.subtitle" max={WIDGET_HOME_LIMITS.subtitle} />}>
          <Input maxLength={WIDGET_HOME_LIMITS.subtitle} placeholder={t(`${K}.screen.subtitlePlaceholder`)} {...register("home.subtitle")} />
        </Field>
        <Field label={t(`${K}.screen.showTo`)}>
          <Select {...register("home.show_to")}>
            {HOME_AUDIENCES.map((audience) => (
              <option key={audience} value={audience}>
                {t(`${K}.showTo.${audience}`)}
              </option>
            ))}
          </Select>
        </Field>
      </CardBody>
    </Card>
  );
}

interface HomePanelProps {
  agentId: string;
  /** The widget payload (its Messenger Page sync status). */
  widget: WidgetOut;
  /** The Home tab is the open one: pickers fetch their lists only then. */
  active: boolean;
}

/** Widget Studio → Home (mockups/tenant/widget-home.html): the settings column; the preview lives in the right pane. */
export function HomePanel({ agentId, widget, active }: HomePanelProps) {
  const workflows = useHomeWorkflows(active);
  const bookings = useAgentBookingActions(agentId, active);
  return (
    <div className="space-y-5">
      <HomeScreen />
      <HomeOptions agentId={agentId} workflows={workflows} bookings={bookings} />
      <HomeMessenger agentId={agentId} profile={readMessengerProfile(widget.messenger_profile)} />
    </div>
  );
}
